import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { ExternalBillingClaimStatus } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimStatus";
import { HyperLinkRedirectControl, HyperLinkRedirectControlPatientProfileSubTabs } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { PopulationBasedFundingHelper } from "@/Common/Helpers/PopulationBasedFundingHelper";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import { BatchEligibilityCheckStatusEnum, PopulationBasedFundingPortfolioSortOrder, PopulationBasedFundingStatus, RedirectTo } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PopulationBasedFundingPortfolioEvent } from "@/Portfolio/Models/PopulationBasedFunding/PopulationBasedFundingPortfolioEvent";
import { PopulationBasedFundingPortfolioActions } from "@/Portfolio/Services/PopulationBasedFundingPortfolioService";

import styles from "./PopulationBasedFundingPortfolioTableHelper.module.scss";

class PopulationBasedFundingPortfolioTableHelper {
    public saveOptionKey: string = "PortfolioPopulationBasedFunding";
    private columnOptions: Immutable.List<MyleTableColumnOptions>;

    constructor() {
        this.columnOptions = this.getColumnsOptions();
    }

    public getTableColumns(): Immutable.List<MyleTableColumn> {
        return this.buildTableColumn(this.columnOptions);
    }

    public getTableRenderOptions(): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: this.columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    public getColumnOptionsForOrderColumnsPopup(): ColumnConfigurationModel[] {
        const columnsOptions = this.getTableColumns().map(tableColums => tableColums.columnOptions).toArray();
        return cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions);
    }

    private getColumnsOptions(): Immutable.List<MyleTableColumnOptions> {
        return cacheStore.Helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getColumnsDefaultOptions());
    }

    private buildTableColumn(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<PopulationBasedFundingPortfolioEvent>>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "Selection":
                        return {
                            renderCell: (item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>) => this.renderSelectCheckboxCell(item),
                            renderHeader: () => this.renderSelectAllCheckboxCell()
                        };

                    case "PatientName":
                        return { renderCell: (item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>) => this.renderPatientNameAsLinkToPatientFile(item) };

                    case "Action":
                        return { renderCell: (item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>) => this.renderActionLinks(item) };

                    default:
                        return { renderCell: (item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>) => this.renderTextCell(column.FieldName, item) };
                }
            }

        );
    }

    private getDateOfBirthWithAge(item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): string {
        if (!item.Model.PatientDateOfBirth) return "";

        const t = (key: string): string => cacheStore.GetString(key);
        const { years, months } = LuxonDateTimeParser.getAgeAsYearMonth(item.Model.PatientDateOfBirth);
        const calculatedAgeString = `${years} ${years <= 1 ? t("YearLabel") : t("YearsLabel")}${months > 0 ? ` ${months}m` : ""}`;

        return `${this.formatDate(item.Model.PatientDateOfBirth).toString()} (${calculatedAgeString})`;
    }

    private renderTextCell(fieldName: string, item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): JSX.Element {
        const text = this.formatCellText(fieldName, item);
        let className = "";
        if (this.eventShouldBeGreyedOut(item.Model)) {
            className = styles.greyedOut;
            if (fieldName != "PhysicianName")
                className += ` ${styles.italic}`;
        }
        return <div className={className}>{text}</div>;
    }

    private formatCellText(fieldName: string, item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): string {
        switch (fieldName) {
            case "ProposalDate":
                return this.formatDate(item.Model.ProposalDate).toString();

            case "ProposalReason":
                return this.getProposalReasonString(item.Model);

            case "PhysicianName":
                if (!item.Model?.EmployeeContactFacetId)
                    return "";

                return Employee.getFullNameWithPrefixContactFacet(item.Model.EmployeeContactFacetId);

            case "Status":
                return this.renderStatusCell(item);

            case "PatientDateOfBirth":
                return this.getDateOfBirthWithAge(item);

            default:
                return item.Model[fieldName];
        }
    }

    private renderStatusCell(item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): string {
        let suffix = "";

        if (!!item.Model.BatchEligibilityCheckStatusEnum) {
            const brcString = cacheStore.GetString("BatchRegistrationCheckAbbrevLabel");
            const valueString = cacheStore.GetString(`${BatchEligibilityCheckStatusEnum[item.Model.BatchEligibilityCheckStatusEnum]}Label`);

            return `${brcString}: ${valueString}`;
        }

        switch (item.Model.ClaimStatus) {
            case ExternalBillingClaimStatus.Submitted:
            case ExternalBillingClaimStatus.Held:
            case ExternalBillingClaimStatus.Unsubmitted:
                suffix = ` (${cacheStore.GetString("PendingLabel")})`;
                break;
            case ExternalBillingClaimStatus.Refused:
            case ExternalBillingClaimStatus.Rejected:
                suffix = ` (${cacheStore.GetString("RefusedLabel")})`;
                break;
            default:
                suffix = "";
        }

        const label = PopulationBasedFundingHelper.getStatusVocabKey(item.Model.Status);

        return `${cacheStore.GetString(label)}${(suffix)}`;
    }

    private formatDate(date?: Date): string {
        if (!date)
            return "";

        const parsedTime = LuxonDateTimeParser.parse(date);
        if (!parsedTime.isValid)
            return "";

        return parsedTime
            .toLocal()
            .toFormat("yyyy/LL/dd");
    }

    private getProposalReasonString(event: PopulationBasedFundingPortfolioEvent): string {
        if (IsNullOrUndefined(event.ProposalReason) && !event.IsInternalProposal) return "";

        let reasonString = "";
        if (!IsNullOrUndefined(event.OverrideReasonEnum)) {
            reasonString = `${cacheStore.GetString("OverriddenLabel")}: ${cacheStore.GetString(this.getOverridenReasonEnumTranslation(event))}`;
        }
        else {
            if (!IsNullOrWhiteSpace(event.AcceptedBy)) {
                reasonString = `${cacheStore.GetString("AcceptedLabel")}: `;
            }
            reasonString += cacheStore.GetString(this.getProposalReasonEnumTranslation(event) ?? "");
        }

        return reasonString;
    }

    private renderSelectCheckboxCell(item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): JSX.Element {
        return (
            <CheckboxControl
                id={item.Model.Id.toString()}
                checked={PopulationBasedFundingPortfolioActions.isEventSelected(item.Model.Id)}
                onChange={() => PopulationBasedFundingPortfolioActions.toggleEventIsSelected(item.Model.Id)}
                disabled={!this.eventIsAProposal(item.Model)}
            />
        );
    }

    private renderSelectAllCheckboxCell(): JSX.Element {
        // the enclosing div is needed for alignment in a legacy component.
        return (
            <div>
                <CheckboxControl
                    onChange={() => PopulationBasedFundingPortfolioActions.toggleSelectAllEvents()}
                    checked={PopulationBasedFundingPortfolioActions.getAreAllEventsSelected()}
                />
            </div>
        );
    }

    private renderPatientNameAsLinkToPatientFile(item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): JSX.Element {
        return (
            <HyperLinkRedirectControl
                target={RedirectTo.Patient}
                redirectValue={item.Model.PatientContactId}
                text={`${item.Model.PatientLastName.trim()}, ${item.Model.PatientFirstName.trim()}`}
                navigateToSubTab={new HyperLinkRedirectControlPatientProfileSubTabs(ProfileTab.PopulationBasedFunding)}
            />
        );
    }

    private renderActionLinks(item: TableDataSourceItem<PopulationBasedFundingPortfolioEvent>): JSX.Element {
        const t = (key: string): string => cacheStore.GetString(key);

        const areAllTasksCompleted = !item.Model.Tasks?.any(task => !task.IsComplete) ?? false;

        return (
            <>
                {(item.Model.IsInternalProposal && !item.Model.AcceptedDate) && (
                    <>
                        <a onClick={() => PopulationBasedFundingPortfolioActions.openRegisterNewPatient(item.Model.PatientContactFacetId, item.Model.Id)}>
                            {t("RegisterLabel")}
                        </a> /&nbsp;
                        <a onClick={() => this.onDeleteEvent(item.Model.Id)}>
                            {t("CancelLabel")}
                        </a> /&nbsp;
                    </>
                )}
                {this.eventIsAProposal(item.Model) && (
                    <>
                        <a onClick={() => PopulationBasedFundingPortfolioActions.acceptProposal(item.Model.Id)}>{t("AcceptLabel")}</a> /&nbsp;
                        <a
                            onClick={() => PopulationBasedFundingPortfolioActions.openOverrideProposalPopup(
                                new PopulationBasedFundingPortfolioEvent(item.Model)
                            )}
                        >{t("OverrideLabel")}
                        </a> /&nbsp;
                    </>
                )}
                <a onClick={() => PopulationBasedFundingPortfolioActions.createTask(item.Model.PatientContactFacetId, item.Model.Id)}>{t("TaskLabel")}</a>
                {item.Model.Tasks?.any() && (
                    <span
                        onClick={e => PopulationBasedFundingPortfolioActions.openTasksListPopup(item.Model, e)}
                        className={(areAllTasksCompleted ? "circle-green" : "circle-orange") + ` ${styles.taskCounterBadge}`}
                    >
                        {item.Model.Tasks.length}
                    </span>
                )}
            </>
        );
    }

    private getProposalReasonEnumTranslation(event: PopulationBasedFundingPortfolioEvent): string {
        if (event.IsInternalProposal)
            return "InternalProposalLabel";

        if (IsNullOrUndefined(event.ProposalReason))
            return "";

        return PopulationBasedFundingHelper.getProposalReasonVocabKey(event.ProposalReason);
    }

    private getOverridenReasonEnumTranslation(event: PopulationBasedFundingPortfolioEvent): string {
        if (IsNullOrUndefined(event.OverrideReasonEnum))
            return "";

        return PopulationBasedFundingHelper.getOverrideReasonVocabKey(event.OverrideReasonEnum);
    }

    private getColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                ...this.getCommonTableColumnOptions("", "Selection", null, 3.3),
                IsFixedInPlace: true,
                RowClickEnabled: false,
                IsSortable: false
            }),

            this.getCommonTableColumnOptions(cacheStore.GetString("PatientLabel"), "PatientName", PopulationBasedFundingPortfolioSortOrder.Patient, 15),

            this.getCommonTableColumnOptions(cacheStore.GetString("DateofBirthLabel"), "PatientDateOfBirth", PopulationBasedFundingPortfolioSortOrder.PatientDateOfBirth, 11),

            new MyleTableColumnOptions({
                ...this.getCommonTableColumnOptions(cacheStore.GetString("PhysicianLabel"), "PhysicianName", null, 20),
                IsSortable: false,
                sortingMethod: null
            }),

            this.getCommonTableColumnOptions(cacheStore.GetString("StatusLabel"), "Status", PopulationBasedFundingPortfolioSortOrder.Status, 10),

            new MyleTableColumnOptions({
                ...this.getCommonTableColumnOptions(cacheStore.GetString("ReasonLabel"), "ProposalReason", null, 10),
                IsSortable: false,
                sortingMethod: null
            }),

            this.getCommonTableColumnOptions(cacheStore.GetString("ProposalDateLabel"), "ProposalDate", PopulationBasedFundingPortfolioSortOrder.ProposalDate, 10),

            new MyleTableColumnOptions({
                ...this.getCommonTableColumnOptions(cacheStore.GetString("ActionLabel"), "Action", null, 10),
                IsSortable: false,
                sortingMethod: null
            })
        ];
    }

    private getCommonTableColumnOptions(headerText: string, fieldName: string, sortColumn: PopulationBasedFundingPortfolioSortOrder, WidthPercentage: number): MyleTableColumnOptions {
        const portfolioModel = portfolioStore.populationBasedFundingPortfolioModel;

        return new MyleTableColumnOptions({
            HeaderText: headerText,
            FieldName: fieldName,
            RowClickEnabled: true,
            IsSortable: true,
            SortColumn: sortColumn,
            SortDirection: portfolioModel.QueryOrderDirection,
            IsSortActive: portfolioModel.QueryOrderType === sortColumn,
            WidthPercentageDefault: WidthPercentage,
            sortingMethod: () => PopulationBasedFundingPortfolioActions.sortEventsList(sortColumn)
        });
    }

    private eventIsAProposal(event: PopulationBasedFundingPortfolioEvent): boolean {
        return event.Status === PopulationBasedFundingStatus.ProposedRegistration
            || event.Status === PopulationBasedFundingStatus.ProposedDeregistration;
    }

    private eventShouldBeGreyedOut(event: PopulationBasedFundingPortfolioEvent): boolean {
        return (
            event.Status != PopulationBasedFundingStatus.ProposedRegistration
            && event.Status != PopulationBasedFundingStatus.ProposedDeregistration
            && !!event.AcceptedDate
        ) || !!event.OverrideReasonEnum;
    }

    private async onDeleteEvent(eventId: string): Promise<void> {
        const response = await PopulationBasedFundingPortfolioActions.deleteEvent(eventId);

        if (response.IsSuccess)
            PopulationBasedFundingPortfolioActions.loadPopulationBasedFundingPortfolioEvents();
    }
}

export { PopulationBasedFundingPortfolioTableHelper };
